import React, { useLayoutEffect, useRef, useState } from "react"
import { CardImage } from "react-bootstrap-icons"
import { toast } from "react-toastify"
import { CameraVideo } from "react-bootstrap-icons"

import styles from "./styles.module.scss"
import clsx from "clsx"
import UploadingStatus from "./UploadingStatus"
import { isMobile } from "react-device-detect"
import { isVideoFile } from "../../util/videoThumbnail"
import { startBackgroundVideoUpload } from "../../util/backgroundVideoUpload"
import { uploadSingleFile } from "../../util/backend_api/posts"
import { mutationRequest } from "../../util/request"
import { api_v1_convert_video_blob_path, api_v1_blob_status_path } from "../../routes"

export const MAX_UPLOAD_BYTES = 262144000 // 250MB, matches Comment::MAX_IMAGE_BYTES server-side

// How often to poll for the Mux-generated poster while composing. Available
// as soon as Mux sets a playback_id (see MuxAsset#poster_url) - well before
// the video is actually ready - so this can show up quickly. Capped so an
// abandoned draft (video attached, never posted) doesn't poll forever.
const MUX_POSTER_POLL_INTERVAL_MS = 5000
const MUX_POSTER_MAX_POLL_ATTEMPTS = 60

const ImagesUpload = ({
  setImageFiles,
  imageFiles,
  imagePreviews,
  setImagePreviews,
  triggerRef,
  hideTriggerButton = false,
  maxFiles = null,
  preprocessFile = null,
}) => {
  const [btnActive, setBtnActive] = useState(true)
  const internalRef = useRef(null)
  const hiddenFileInput = triggerRef || internalRef
  const [height, setHeight] = useState(0)
  const ref = useRef(null)

  useLayoutEffect(() => {
    if (isMobile) {
      setHeight(window.innerWidth / 3)
    } else {
      let width = (((window.innerWidth * 5) / 12) * 2) / 3
      setHeight((width / 3) * 0.725)
    }
  })

  const handleFileClick = () => {
    hiddenFileInput.current.click()
  }

  const onUploadImages = fileList => {
    let fileListArray = Array.from(fileList)
    if (maxFiles !== null) {
      const remaining = maxFiles - imagePreviews.length
      if (fileListArray.length > remaining) {
        const msg = remaining > 0
          ? `You can only add ${remaining} more file${remaining === 1 ? "" : "s"} (max ${maxFiles} per comment)`
          : `You've reached the maximum of ${maxFiles} files per comment`
        toast.error(msg)
        fileListArray = fileListArray.slice(0, remaining)
      }
    }
    fileListArray.forEach(async attachment => {
      if (attachment.size > MAX_UPLOAD_BYTES) {
        toast.error("Sorry! The file must be smaller than 250MB")
      } else {
        setBtnActive(false)
        try {
          const file = preprocessFile ? await preprocessFile(attachment) : attachment
          handleImageUpload(file)
        } catch (e) {
          toast.error(e.message || "Sorry! That file couldn't be processed.")
        } finally {
          setBtnActive(true)
        }
      }
    })
    setImageFiles(fileListArray)
  }

  const onRemoveImage = index => {
    setImagePreviews(imagePreviews.filter((_item, i) => i !== index))
    setImageFiles(imageFiles.filter((_item, i) => i !== index))
    setBtnActive(true)
  }

  // Polls for the Mux-generated poster while composing, so it can replace
  // the generic video icon as soon as it's available - not just after
  // Post Comment is clicked. Stops once found, once the file is removed
  // from the form (checked via the functional setImagePreviews updater, so
  // it always sees current state), or after MUX_POSTER_MAX_POLL_ATTEMPTS.
  const pollForMuxPoster = (file, signedId, attempt = 0) => {
    if (attempt >= MUX_POSTER_MAX_POLL_ATTEMPTS) return
    mutationRequest({
      path: api_v1_blob_status_path(signedId),
      options: { method: "GET" },
    }).then(result => {
      if (result?.poster_url) {
        setImagePreviews(currentImagePreviews =>
          currentImagePreviews.map(preview =>
            preview.file === file ? { ...preview, muxPosterUrl: result.poster_url } : preview,
          ),
        )
        return
      }
      setImagePreviews(currentImagePreviews => {
        if (currentImagePreviews.some(preview => preview.file === file)) {
          window.setTimeout(
            () => pollForMuxPoster(file, signedId, attempt + 1),
            MUX_POSTER_POLL_INTERVAL_MS,
          )
        }
        return currentImagePreviews
      })
    })
  }

  const handleImageUpload = file => {
    if (file !== null) {
      const reader = new FileReader()
      reader.onload = e => {
        setImagePreviews(currentImagePreviews => [
          ...currentImagePreviews,
          {
            data: e.target.result,
            filename: file.name,
            file: file,
            isUploading: false,
            isUploaded: false,
            contentTypeGeneric: isVideoFile(file) ? "video" : file.type.split("/")[0],
            contentTypeSpecific: file.type.split("/")[1] || file.name.split(".").pop(),
          },
        ])
      }
      reader.readAsDataURL(file)

      // Videos start uploading immediately, silently - not gated on "Post
      // Comment" like images are. isUploading/uploadProgress are left
      // untouched here on purpose so nothing about this shows in the UI;
      // uploadImages() picks this same upload back up at submit time
      // (see backgroundVideoUpload.ts) and only then decides whether to
      // reveal progress (still in flight) or skip straight past it (already
      // done).
      if (isVideoFile(file)) {
        const backgroundUpload = startBackgroundVideoUpload(file, () =>
          uploadSingleFile(file, file.name),
        )
        backgroundUpload.promise.then(outcome => {
          if ("error" in outcome) return
          // Fire-and-forget: kicks off Mux processing as early as possible,
          // before this video is attached to any comment/post. If this
          // never lands, Comments::ConvertVideoJob/Posts::ConvertVideoJob
          // still trigger the same (idempotent) conversion once the
          // comment/post is actually created - nothing is lost.
          mutationRequest({
            path: api_v1_convert_video_blob_path(outcome.signedId),
            options: { method: "POST" },
          }).catch(() => {})
          pollForMuxPoster(file, outcome.signedId)
        })
      }
    }
  }

  return (
    <>
      {imagePreviews?.length > 0 && (
        <>
          <div className="flex flex-wrap gap-3 mb-2" ref={ref}>
            {imagePreviews.map((imagePreview, index) => (
              <React.Fragment
                key={imagePreview.filename || imagePreview.signedId}
              >
                {imagePreview.contentTypeGeneric === "video" ? (
                  <div
                    className={clsx(
                      styles.imageContainer,
                      imagePreview.isUploading || imagePreview.isUploaded
                        ? styles.imageContainerCentered
                        : "",
                                          )}
                    style={{ height: height }}
                  >
                    {imagePreview.muxPosterUrl ? (
                      <>
                        <img
                          src={imagePreview.muxPosterUrl}
                          alt={imagePreview.filename}
                          title={imagePreview.filename}
                          className={clsx(
                            styles.image,
                            imagePreview.isUploading ? styles.uploadingImage : "",
                          )}
                        />
                        <CameraVideo className={styles.videoPosterIcon} />
                      </>
                    ) : (
                      <div className={styles.videoThumbReplacementContainer}>
                        <div>
                          <CameraVideo className={styles.videoThumbReplacement} />
                          <div className={styles.videoThumbReplacementText}>
                            {imagePreview.filename}
                          </div>
                        </div>
                      </div>
                    )}
                    <UploadingStatus
                      imagePreview={imagePreview}
                      index={index}
                      onRemoveImage={onRemoveImage}
                    />
                  </div>
                ) : (
                  <div
                    className={clsx(
                      styles.imageContainer,
                      imagePreview.isUploading || imagePreview.isUploaded
                        ? styles.imageContainerCentered
                        : "",
                                          )}
                    style={{
                      height: height,
                    }}
                  >
                    <img
                      src={imagePreview.url || imagePreview.data}
                      alt={imagePreview.filename || imagePreview.filename}
                      title={imagePreview.filename || imagePreview.filename}
                      className={clsx(
                        styles.image,
                        imagePreview.isUploading ? styles.uploadingImage : "",
                      )}
                    />
                    <UploadingStatus
                      imagePreview={imagePreview}
                      index={index}
                      onRemoveImage={onRemoveImage}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </>
      )}

      {!hideTriggerButton && (
        <button
          type="button"
          disabled={!btnActive}
          onClick={handleFileClick}
          className="bg-green-200 flex justify-center py-1 w-full focus:outline-none"
        >
          <CardImage size="24" className="text-gray-700 mr-1" /> Add{" "}
          {imagePreviews?.length > 0 && "More"} Images/Video
        </button>
      )}
      <input
        type="file"
        ref={hiddenFileInput}
        accept="image/*,video/*"
        style={{ display: "none" }}
        onChange={e => { onUploadImages(e.target.files); e.target.value = "" }}
        multiple={true}
        disabled={maxFiles !== null && imagePreviews.length >= maxFiles}
      />
    </>
  )
}

export default ImagesUpload
